import React, { useState, useEffect } from 'react';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import Canvas from '../../layouts/dahboard_canvas';
import { useBaseUrl, getJsonAuthHeaders } from '../../../utils/utils';

type CETab = "dashboard" | "courses" | "certifications" | "network";

interface Course {
    id: string; title: string; provider: string; category: string; credits: number;
    duration: string; status: string; progress: number; deadline: string | null; is_network: boolean;
}

interface Certification {
    id: string; name: string; issued_by: string; date_issued: string; expiry_date: string;
    status: string; credits_required: number; credits_earned: number;
}

const ContinuingEducation: React.FC = () => {
    const [activeTab, setActiveTab] = useState<CETab>('dashboard');
    const [courses, setCourses] = useState<Course[]>([]);
    const [certifications, setCertifications] = useState<Certification[]>([]);
    const [loading, setLoading] = useState(true);
    const baseUrl = useBaseUrl();

    useEffect(() => {
        document.title = "Uzima - Continuing Education";
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const headers = await getJsonAuthHeaders();
            const [cRes, certRes] = await Promise.allSettled([
                fetch(`${baseUrl}core/api/continuing-education/courses/`, { headers }),
                fetch(`${baseUrl}core/api/continuing-education/certifications/`, { headers }),
            ]);
            if (cRes.status === 'fulfilled' && cRes.value.ok) { const d = await cRes.value.json(); if (d.status === 'success') setCourses(d.data || []); }
            if (certRes.status === 'fulfilled' && certRes.value.ok) { const d = await certRes.value.json(); if (d.status === 'success') setCertifications(d.data || []); }
        } catch (err) { console.error("Load error:", err); }
        finally { setLoading(false); }
    };

    const creditsEarned = courses.filter(c => c.status === 'completed').reduce((s, c) => s + (c.credits || 0), 0);
    const inProgress = courses.filter(c => c.status === 'in-progress');
    const validCerts = certifications.filter(c => c.status === 'valid');

    const tabs: Array<{ key: CETab; label: string; icon: string }> = [
        { key: "dashboard", label: "My Progress", icon: "bx bx-bar-chart-alt-2" },
        { key: "courses", label: "Courses", icon: "bx bx-book-open" },
        { key: "certifications", label: "Certifications", icon: "bx bx-shield-quarter" },
        { key: "network", label: "Network Learning", icon: "bx bx-share-alt" },
    ];

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="main-content">
                <div className="page-content">
                    <div className="sc-wrap">
                        <div className="sc-header">
                            <div><h1>Continuing Education</h1><p>CME/CPD courses, certifications, and professional development</p></div>
                        </div>
                        <div className="sc-stats">
                            <div className="sc-stat"><div className="sc-stat-icon teal"><i className="bx bx-trophy"></i></div><div className="sc-stat-value">{creditsEarned}</div><div className="sc-stat-label">Credits Earned</div></div>
                            <div className="sc-stat"><div className="sc-stat-icon blue"><i className="bx bx-book-open"></i></div><div className="sc-stat-value">{inProgress.length}</div><div className="sc-stat-label">In Progress</div></div>
                            <div className="sc-stat"><div className="sc-stat-icon violet"><i className="bx bx-shield-quarter"></i></div><div className="sc-stat-value">{validCerts.length}</div><div className="sc-stat-label">Active Certs</div></div>
                            <div className="sc-stat"><div className="sc-stat-icon amber"><i className="bx bx-list-check"></i></div><div className="sc-stat-value">{courses.length}</div><div className="sc-stat-label">Total Courses</div></div>
                        </div>
                        <div className="sc-tabs">{tabs.map(t => (<button key={t.key} className={`sc-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}><i className={t.icon}></i> {t.label}</button>))}</div>
                        {loading ? <div className="sc-spinner"></div> : (
                            <>
                                {activeTab === 'dashboard' && (
                                    <div className="sc-panel"><div className="sc-panel-head"><h3>My Progress</h3></div>
                                        <div className="sc-panel-body">
                                            {inProgress.length === 0 ? (
                                                <div className="sc-empty"><i className="bx bx-book-reader"></i><h3>No Courses In Progress</h3><p>Browse available courses to start your continuing education journey.</p></div>
                                            ) : (
                                                <div className="sc-list-stack">{inProgress.map(c => (
                                                    <div className="sc-list-item" key={c.id} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <div><strong>{c.title}</strong><small style={{ display: 'block', color: 'var(--uzima-shell-muted)' }}>{c.provider} &middot; {c.credits} credits &middot; {c.duration}</small></div>
                                                            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--uzima-shell-accent)' }}>{c.progress}%</span>
                                                        </div>
                                                        <div style={{ background: 'var(--uzima-shell-border)', borderRadius: '6px', height: '6px', marginTop: '8px' }}>
                                                            <div style={{ background: 'var(--uzima-shell-accent)', borderRadius: '6px', height: '100%', width: `${c.progress}%`, transition: 'width 0.3s' }}></div>
                                                        </div>
                                                        {c.deadline && <small style={{ color: 'var(--uzima-shell-muted)', marginTop: '4px' }}>Due: {c.deadline}</small>}
                                                    </div>
                                                ))}</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {activeTab === 'courses' && (
                                    <div className="sc-panel"><div className="sc-panel-head"><h3>Available Courses</h3></div>
                                        <div className="sc-panel-body">{courses.length === 0 ? (
                                            <div className="sc-empty"><i className="bx bx-book-open"></i><h3>No Courses Available</h3><p>Courses will appear here when published by the network.</p></div>
                                        ) : (
                                            <div className="sc-grid">{courses.map(c => (
                                                <div className="sc-card" key={c.id}><div className="sc-card-body">
                                                    <div className="sc-card-row"><div><div className="sc-card-title">{c.title}</div><div className="sc-card-desc">{c.provider}</div></div>
                                                        <span className={`sc-badge-status ${c.status === 'completed' ? 'agreed' : c.status === 'in-progress' ? 'pending' : 'negotiating'}`}>{c.status}</span></div>
                                                    <div className="sc-card-meta"><span><i className="bx bx-time-five"></i> {c.duration}</span><span><i className="bx bx-trophy"></i> {c.credits} credits</span><span className="sc-chip soft">{c.category}</span></div>
                                                </div><div className="sc-card-foot"><span className="sc-chip">{c.status}</span>
                                                    <button className="sc-btn sc-btn-primary sc-btn-sm">{c.status === 'completed' ? 'Certificate' : c.status === 'in-progress' ? 'Continue' : 'Start'}</button></div></div>
                                            ))}</div>
                                        )}</div>
                                    </div>
                                )}
                                {activeTab === 'certifications' && (
                                    <div className="sc-panel"><div className="sc-panel-head"><h3>Certifications</h3></div>
                                        <div className="sc-panel-body">{certifications.length === 0 ? (
                                            <div className="sc-empty"><i className="bx bx-shield-quarter"></i><h3>No Certifications</h3><p>Your professional certifications will be tracked here.</p></div>
                                        ) : (
                                            <div className="sc-table-wrap"><table className="sc-table"><thead><tr><th>Certification</th><th>Issued By</th><th>Issued</th><th>Expiry</th><th>Status</th></tr></thead>
                                                <tbody>{certifications.map(c => (
                                                    <tr key={c.id}><td><strong>{c.name}</strong></td><td>{c.issued_by}</td><td>{c.date_issued}</td><td>{c.expiry_date}</td>
                                                        <td><span className={`sc-badge-status ${c.status === 'valid' ? 'agreed' : c.status === 'expiring' ? 'pending' : 'expired'}`}>{c.status}</span></td></tr>
                                                ))}</tbody></table></div>
                                        )}</div>
                                    </div>
                                )}
                                {activeTab === 'network' && (
                                    <div className="sc-panel"><div className="sc-panel-body">
                                        <div className="sc-empty"><i className="bx bx-globe"></i><h3>Network Learning Hub</h3><p>Courses and training shared across the Uzima provider network. Access CME/CPD content from partner institutions and professional bodies.</p></div>
                                    </div></div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <DashboardFooter />
            </div>
            <Canvas />
        </>
    );
};

export default ContinuingEducation;
